import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import QRCode from "qrcode";

/**
 * Desktop-side "Pair another device" dialog.
 *
 * Calls /api/pair/issue (which reads the desktop's HttpOnly cookie server-side
 * and stashes the JWT in KV against a one-time code), then displays:
 *
 *   1. A QR code encoding `${origin}/pair?code=<code>`. The user opens their
 *      phone's default camera app, points it at the QR, taps the "Open URL"
 *      prompt, and the phone browser lands on /pair which auto-redeems and
 *      sets the cookie on the mobile device.
 *   2. The same code as plain text — fallback for phones whose camera doesn't
 *      do QR. The user can type it into /pair manually.
 *
 * Industry pattern: WhatsApp Web / Discord / Signal Desktop "scan from
 * another device" UX.
 *
 * Code lifetime
 * -------------
 * Server stores the code with a 5-minute KV TTL. Once redeemed (mobile POSTs
 * /api/pair/redeem) the entry is deleted — single-use. We show a countdown
 * so the user knows when to tap "Generate new code". No background refresh
 * because the desktop user is presumably watching the QR with the phone in
 * hand; if they walk away and come back, they tap "Generate" again.
 */
export interface PairDeviceDialogProps {
  open: boolean;
  onClose: () => void;
}

interface IssueResponse {
  code: string;
  expiresAt: number; // unix seconds
}

export function PairDeviceDialog({ open, onClose }: PairDeviceDialogProps) {
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "ready"; code: string; expiresAt: number; qrDataUrl: string }
    | { kind: "error"; msg: string }
  >({ kind: "idle" });
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const cancelledRef = useRef(false);

  // Re-issue every time the dialog opens so the code is fresh.
  useEffect(() => {
    if (!open) {
      setState({ kind: "idle" });
      return;
    }
    cancelledRef.current = false;
    void issue();
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Tick once per second while the dialog is open + a code is active so the
  // countdown updates. Stop ticking when the code expires.
  useEffect(() => {
    if (state.kind !== "ready") return;
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, [state.kind]);

  async function issue() {
    setState({ kind: "loading" });
    try {
      const res = await fetch("/api/pair/issue", {
        method: "POST",
        credentials: "include",
      });
      if (res.status === 401) {
        setState({
          kind: "error",
          msg: "You need to sign in on this computer first before you can pair a phone.",
        });
        return;
      }
      if (res.status === 503) {
        setState({
          kind: "error",
          msg: "Pairing is not configured on the server (WHB_PAIR KV namespace missing).",
        });
        return;
      }
      if (!res.ok) {
        setState({ kind: "error", msg: `Couldn't issue pairing code (${res.status}).` });
        return;
      }
      const body = (await res.json()) as IssueResponse;
      const url = `${window.location.origin}/pair?code=${encodeURIComponent(body.code)}`;
      // Generate QR as a data: URL so we can drop it straight into an <img src>.
      // Use error correction level "M" (medium, ~15%) — good balance between
      // size and tolerance for camera misalignment / small print bleed.
      const qrDataUrl = await QRCode.toDataURL(url, {
        errorCorrectionLevel: "M",
        margin: 1,
        width: 280,
      });
      if (cancelledRef.current) return;
      setState({
        kind: "ready",
        code: body.code,
        expiresAt: body.expiresAt,
        qrDataUrl,
      });
    } catch {
      setState({ kind: "error", msg: "Network error issuing pairing code." });
    }
  }

  const remainingSec = state.kind === "ready" ? Math.max(0, state.expiresAt - now) : 0;
  const expired = state.kind === "ready" && remainingSec <= 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Pair another device</DialogTitle>
      <DialogContent dividers>
        {state.kind === "loading" && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {state.kind === "error" && <Alert severity="error">{state.msg}</Alert>}

        {state.kind === "ready" && (
          <Stack spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
              On your phone, open the camera and point it at the QR code below.
              Tap the "Open link" prompt that appears.
            </Typography>

            <Box
              sx={{
                p: 2,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                opacity: expired ? 0.4 : 1,
                position: "relative",
              }}
            >
              <img src={state.qrDataUrl} alt="Pairing QR code" width={240} height={240} />
              {expired && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(0,0,0,0.45)",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "common.white", fontWeight: 600 }}>
                    Expired
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary">
              Or type this code at <strong>whb-client.pages.dev/pair</strong>
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "ui-monospace, SFMono-Regular, monospace",
                letterSpacing: "0.1em",
                userSelect: "all",
                opacity: expired ? 0.4 : 1,
              }}
            >
              {state.code}
            </Typography>

            <Typography variant="caption" color={expired ? "error.main" : "text.secondary"}>
              {expired
                ? "Code expired. Generate a new one to try again."
                : `Code expires in ${formatRemaining(remainingSec)}`}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {state.kind === "ready" && (
          <Button onClick={() => void issue()}>Generate new code</Button>
        )}
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function formatRemaining(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
